import { formatAgentIdToName, STATUS_ICONS } from './tagParser';

export { formatAgentIdToName, STATUS_ICONS };

const REVERSE_PROMPT_PATTERN = /(反推|拆解|解析|逆向).*(提示词|prompt)|(提示词|prompt).*(反推|拆解|解析|逆向)|reverse[\s-]?engineer/i;
export const isReversePromptRequest = (text: string) => REVERSE_PROMPT_PATTERN.test(text);

export const buildAssistantIntro = ({
  userText,
  intentName,
  hasAttachments,
  hasReferences,
}: {
  userText: string;
  intentName?: string;
  hasAttachments: boolean;
  hasReferences: boolean;
}) => {
  const hasVisualContext = hasAttachments || hasReferences;
  if (hasVisualContext && (intentName === 'analyze_image' || isReversePromptRequest(userText))) {
    return '我来帮你反推这张图片的提示词。这张作品很有氛围，我会先从画面主体、环境背景、艺术风格、光影色彩、构图视角这 5 个维度拆解，再整理成可直接使用的提示词。';
  }
  if (intentName === 'search') return '我先帮你搜索和筛选相关信息，再把重点整理给你。';
  if (intentName === 'generate_image') return '我先帮你梳理这次出图方向，再整理成更容易直接使用的图像提示词。';
  if (intentName === 'generate_video') return '我先帮你拆解视频的主体、镜头、运动和氛围，再整理成更适合生成的视频描述。';
  if (intentName === 'canvas_operation') return '我先理解你的目标和上下文，然后为你搭建对应的工作流。';
  if (hasVisualContext) return '我先看一下你给的参考内容，整理重点之后再开始处理。';
  return '我先理解一下你的需求，整理思路后马上开始处理。';
};

export const formatFinalAssistantText = ({
  text,
  intentName,
  userText,
  hasVisualContext,
}: {
  text: string;
  intentName?: string;
  userText: string;
  hasVisualContext: boolean;
}) => {
  if (hasVisualContext && intentName === 'analyze_image' && isReversePromptRequest(userText) && !text.trimStart().startsWith('分析完成')) {
    return `分析完成。以下是反推的提示词：\n\n${text}`;
  }
  return text;
};

export const formatDuration = (ms: number): string => {
  if (ms < 1000) {
    return `${ms}ms`;
  }

  const seconds = ms / 1000;
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);

  if (minutes < 60) {
    if (remainingSeconds === 0) {
      return `${minutes}m`;
    }
    return `${minutes}m ${remainingSeconds}s`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${remainingMinutes}m`;
};

export const formatSessionDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr + (dateStr.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateStr) ? '' : 'Z'));
    return d.toLocaleString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

export const getTaskStatusIcon = (status: string): string => {
  return STATUS_ICONS[status] || '⏳';
};

export const getTaskStatusColor = (status: string): string => {
  switch (status) {
    case 'searching': return 'text-blue-500';
    case 'running': return 'text-yellow-500';
    case 'completed': return 'text-emerald-500';
    case 'failed':
    case 'rejected':
    case 'timeout': return 'text-red-500';
    case 'pending':
    case 'cancelled': return 'text-gray-400';
    case 'clarifying': return 'text-purple-500';
    default: return 'text-gray-500';
  }
};

export const getTaskStatusLabel = (status: string): string => {
  switch (status) {
    case 'searching': return '搜索中';
    case 'running': return '执行中';
    case 'completed': return '已完成';
    case 'failed': return '失败';
    case 'rejected': return '已拒绝';
    case 'timeout': return '已超时';
    case 'pending': return '等待中';
    case 'cancelled': return '已取消';
    case 'clarifying': return '补充信息中';
    default: return status;
  }
};

export const getTaskStatusBorderBg = (status: string): string => {
  switch (status) {
    case 'searching': return 'border-blue-500/30 bg-blue-500/5';
    case 'running': return 'border-yellow-500/30 bg-yellow-500/5';
    case 'completed': return 'border-emerald-500/30 bg-emerald-500/5';
    case 'failed':
    case 'rejected':
    case 'timeout': return 'border-red-500/30 bg-red-500/5';
    case 'pending':
    case 'cancelled': return 'border-gray-500/30 bg-gray-500/5';
    case 'clarifying': return 'border-purple-500/30 bg-purple-500/5';
    default: return 'border-gray-500/30 bg-gray-500/5';
  }
};
